import type {QRPayloadIssue} from '@Libraries/qr-code/qr-code.types';
import type {QRDesignTile} from '@Components/qr-code-generator/qr-code-generator.types';

/** Written by the landing page hero (`FreeToolType::getFrontEndEntry()`); the tool mounts into it. */
export const UTM_LINK_BUILDER_CONTAINER_ID = 'utm-link-builder';

/** Dataset key of the mount point's `data-template-tiles`: the QR designs the built link can be put into, as JSON. */
export const UTM_TEMPLATE_TILES_DATA_ATTRIBUTE = 'templateTiles';

/** Dataset keys of the mount point's `data-video-*-url` attributes, which carry the demo video's CDN URLs. */
export enum UtmVideoDataAttribute {
  WEBM = 'videoWebmUrl',
  HEVC = 'videoHevcUrl',
  POSTER = 'videoPosterUrl',
}

export interface UtmLinkVideoSources {
  webmUrl: string;
  hevcUrl: string;
  posterUrl: string;
}

export interface UtmLinkBuilderProps {
  /** Null when the page HTML predates the video, so the tool still works without it. */
  videoSources: UtmLinkVideoSources | null;
  templateTiles: QRDesignTile[];
}

export enum UtmParameter {
  SOURCE = 'utm_source',
  MEDIUM = 'utm_medium',
  CAMPAIGN = 'utm_campaign',
  ID = 'utm_id',
  TERM = 'utm_term',
  CONTENT = 'utm_content',
}

export type UtmParameterValues = Record<UtmParameter, string>;

export interface UtmLinkInput {
  websiteUrl: string;
  parameterValues: UtmParameterValues;
}

export interface UtmLinkResult {
  /** What the visitor has typed so far as one link, rebuilt on every keystroke. Null only while every field is empty. */
  previewLink: string | null;
  /** The link to copy: null until the website URL is a real web address and every required parameter is filled in. */
  completeLink: string | null;
  websiteUrlIssues: QRPayloadIssue[];
}

export interface UtmParameterField {
  parameter: UtmParameter;
  labelKey: string;
  placeholderKey: string;
  helpKey: string;
}

export const REQUIRED_UTM_PARAMETERS: UtmParameter[] = [UtmParameter.SOURCE, UtmParameter.MEDIUM, UtmParameter.CAMPAIGN];

/** Appended to a required field's label. A symbol, not a word, so it needs no translation. */
export const REQUIRED_FIELD_MARKER = '*';

/**
 * Required first: the form sets three to a row, so the required three fill the first row and the optional three the second.
 * The built link lists its parameters in this same order.
 */
export const UTM_PARAMETER_FIELDS: UtmParameterField[] = [
  {
    parameter: UtmParameter.SOURCE,
    labelKey: 'pmwjs_utm_builder_source_label',
    placeholderKey: 'pmwjs_utm_builder_source_placeholder',
    helpKey: 'pmwjs_utm_builder_source_help',
  },
  {
    parameter: UtmParameter.MEDIUM,
    labelKey: 'pmwjs_utm_builder_medium_label',
    placeholderKey: 'pmwjs_utm_builder_medium_placeholder',
    helpKey: 'pmwjs_utm_builder_medium_help',
  },
  {
    parameter: UtmParameter.CAMPAIGN,
    labelKey: 'pmwjs_utm_builder_campaign_label',
    placeholderKey: 'pmwjs_utm_builder_campaign_placeholder',
    helpKey: 'pmwjs_utm_builder_campaign_help',
  },
  {
    parameter: UtmParameter.ID,
    labelKey: 'pmwjs_utm_builder_id_label',
    placeholderKey: 'pmwjs_utm_builder_id_placeholder',
    helpKey: 'pmwjs_utm_builder_id_help',
  },
  {
    parameter: UtmParameter.TERM,
    labelKey: 'pmwjs_utm_builder_term_label',
    placeholderKey: 'pmwjs_utm_builder_term_placeholder',
    helpKey: 'pmwjs_utm_builder_term_help',
  },
  {
    parameter: UtmParameter.CONTENT,
    labelKey: 'pmwjs_utm_builder_content_label',
    placeholderKey: 'pmwjs_utm_builder_content_placeholder',
    helpKey: 'pmwjs_utm_builder_content_help',
  },
];

export const EMPTY_UTM_LINK_INPUT: UtmLinkInput = {
  websiteUrl: '',
  parameterValues: {
    [UtmParameter.SOURCE]: '',
    [UtmParameter.MEDIUM]: '',
    [UtmParameter.CAMPAIGN]: '',
    [UtmParameter.ID]: '',
    [UtmParameter.TERM]: '',
    [UtmParameter.CONTENT]: '',
  },
};

/** The website URL field's `name` in GA4; the six parameter fields go by their `UtmParameter` value. */
export enum UtmFormField {
  WEBSITE_URL = 'website_url',
}

export type UtmFormFieldName = UtmFormField | UtmParameter;

/** GA4 `result` of a finished field edit. Only the website URL can fail its check; the parameters take any text. */
export enum UtmFieldCheckResult {
  VALID = 'valid',
  INVALID = 'invalid',
}

/** GA4 `type`: whether the link was ready to copy when the visitor picked a design or left for the gallery. */
export enum UtmLinkState {
  COMPLETE = 'link_complete',
  INCOMPLETE = 'link_incomplete',
}

export interface UtmDesignSelection {
  linkState: UtmLinkState;
  designHashedId: string;
  /** Place in the designs row, 1 being the first. */
  position: number;
}

export interface UtmFieldFilledTracking {
  onFieldFocus: (fieldName: UtmFormFieldName, value: string) => void;
  onFieldBlur: (fieldName: UtmFormFieldName, value: string) => void;
}
